import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Landing from "./pages/Landing";

// Lazy-loaded routes to reduce initial JS bundle size
const Discover = lazy(() => import("./pages/Discover"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const NewProduct = lazy(() => import("./pages/NewProduct"));
const EditProduct = lazy(() => import("./pages/EditProduct"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Signup = lazy(() => import("./pages/Signup"));
const Admin = lazy(() => import("./pages/Admin"));
const Settings = lazy(() => import("./pages/Settings"));
const Missions = lazy(() => import("./pages/Missions"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Contact = lazy(() => import("./pages/Contact"));
const Faq = lazy(() => import("./pages/Faq"));
const Promotions = lazy(() => import("./pages/Promotions"));
const Withdrawals = lazy(() => import("./pages/Withdrawals"));
const Credits = lazy(() => import("./pages/Credits"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Bounties = lazy(() => import("./pages/Bounties"));
const NewBounty = lazy(() => import("./pages/NewBounty"));
const BountyDetail = lazy(() => import("./pages/BountyDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/products/new" element={<NewProduct />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              <Route path="/products/:id/edit" element={<EditProduct />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/missions" element={<Missions />} />
              <Route path="/promotions" element={<Promotions />} />
              <Route path="/withdrawals" element={<Withdrawals />} />
              <Route path="/credits" element={<Credits />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/bounties" element={<Bounties />} />
              <Route path="/bounties/new" element={<NewBounty />} />
              <Route path="/bounties/:id" element={<BountyDetail />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
